import sessionApi, { listResults } from './apiClient';
import { Discrepancy, EvidenceRecord, InspectionMission } from '../types/domain';

export interface InspectionMissionPayload {
  target_station_unit: string;
  department: number;
  date_of_physical_count: string;
  remarks?: string;
}

export interface Form18RowPayload {
  mission: number;
  inspected_officer?: number;
  officer_name_input?: string;
  entered_firearm_serial_number: string;
  physical_status: string;
  are_presented: boolean;
  remarks?: string;
}

export type InspectionReportDetail =
  | 'verified'
  | 'conflict'
  | 'unrecorded'
  | 'lost'
  | 'stolen'
  | 'beyond_economical_repair';

export interface InspectionSubmissionPayload {
  department: number;
  actual_inspection_date: string;
  firearm_serial_number: string;
  officer_identifier: string;
  report_detail: InspectionReportDetail;
  remarks?: string;
  are_presented?: boolean;
  are_file?: File | null;
  supporting_firearm_image?: File | null;
}

export interface InspectionCustodyContext {
  issuance_id: number | null;
  issuance_status: string;
  expected_officer_id: number | null;
  expected_officer_name: string;
  expected_officer_badge_number: string;
  firearm_id: number | null;
  firearm_serial_number: string;
  firearm_status: string;
  registry_assigned_to_id: number | null;
  registry_assigned_to_name: string;
  department_id: number | null;
  department_name: string;
  active_issuance_count: number;
  are_id: number | null;
  are_status: string;
  validation_state: string;
  reasons: string[];
}

export interface RetainAuthoritativePayload {
  resolution_notes: string;
}

export interface ReferCustodyCorrectionPayload {
  referral_notes: string;
  correction_workflow: 'ISSUANCE' | 'RETURN' | 'UNSPECIFIED';
  correction_instruction: string;
  correction_target_officer?: number | null;
}

export interface CorrectionPlanPayload {
  correction_workflow: 'ISSUANCE' | 'RETURN' | 'UNSPECIFIED';
  correction_instruction: string;
  correction_target_officer?: number | null;
}

export interface RevalidateCorrectionPayload {
  resolution_notes?: string;
}

export const inspectionService = {
  getInspectionMissions: async (): Promise<InspectionMission[]> => {
    const response = await sessionApi.get('/inspection-missions/');
    return listResults<InspectionMission>(response.data);
  },

  createInspectionMission: async (payload: InspectionMissionPayload): Promise<InspectionMission> => {
    const response = await sessionApi.post('/inspection-missions/', payload);
    return response.data;
  },

  getForm18Rows: async (filters: { mission?: number } = {}) => {
    const response = await sessionApi.get('/inspection-form18-rows/', { params: filters });
    return listResults<any>(response.data);
  },

  createForm18Row: async (payload: Form18RowPayload) => {
    const response = await sessionApi.post('/inspection-form18-rows/', payload);
    return response.data;
  },

  getDepartments: async () => {
    try {
      const response = await sessionApi.get('/departments/');
      return listResults<any>(response.data);
    } catch (error) {
      const profileResponse = await sessionApi.get('/auth/profile/');
      const profileDepartments = profileResponse.data?.user?.departments || [];
      if (profileDepartments.length > 0) return profileDepartments;
      throw error;
    }
  },

  getReconciliationReports: async () => {
    const response = await sessionApi.get('/reconciliation-reports/');
    return listResults<any>(response.data);
  },

  generateReconciliationReport: async (mission: number) => {
    const response = await sessionApi.post('/reconciliation-reports/', { mission });
    return response.data;
  },

  getGeneratedReports: async (
    filters: { report_type?: string; inspection_mission?: number; reconciliation_report?: number } = {}
  ) => {
    const response = await sessionApi.get('/generated-reports/', { params: filters });
    return listResults<any>(response.data);
  },

  downloadGeneratedReport: async (id: number): Promise<Blob> => {
    const response = await sessionApi.get(`/generated-reports/${id}/download/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getEvidenceRecords: async (filters: { row?: number; form18_row?: number; mission?: number } = {}): Promise<EvidenceRecord[]> => {
    const response = await sessionApi.get('/inspection-photo-evidence/', { params: filters });
    return listResults<EvidenceRecord>(response.data);
  },

  uploadEvidence: async (payload: { form18_row: number; image: File; caption?: string }) => {
    const formData = new FormData();
    formData.append('form18_row', String(payload.form18_row));
    formData.append('image', payload.image);
    if (payload.caption) formData.append('caption', payload.caption);
    const response = await sessionApi.post('/inspection-photo-evidence/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getInspectionSubmissions: async () => {
    const response = await sessionApi.get('/inspection-submissions/');
    return listResults<any>(response.data);
  },

  getCustodyContext: async (params: {
    firearm_id?: number;
    serial_number?: string;
    department_id?: number;
    observed_officer_id?: number;
  }): Promise<InspectionCustodyContext> => {
    const response = await sessionApi.get('/inspection-custody-context/', { params });
    return response.data;
  },

  submitInspection: async (payload: InspectionSubmissionPayload) => {
    const formData = new FormData();
    formData.append('department', String(payload.department));
    formData.append('actual_inspection_date', payload.actual_inspection_date);
    formData.append('firearm_serial_number', payload.firearm_serial_number);
    formData.append('officer_identifier', payload.officer_identifier);
    formData.append('report_detail', payload.report_detail);
    formData.append('are_presented', String(payload.are_presented !== false));
    if (payload.remarks) formData.append('remarks', payload.remarks);
    if (payload.are_file) formData.append('are_file', payload.are_file);
    if (payload.supporting_firearm_image) {
      formData.append('supporting_firearm_image', payload.supporting_firearm_image);
    }
    const response = await sessionApi.post('/inspection-submissions/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getInspectionEvidencePreview: async (id: number, kind: 'ARE' | 'photo'): Promise<Blob> => {
    const response = await sessionApi.get(`/inspection-submissions/${id}/evidence-preview/`, {
      params: { kind },
      responseType: 'blob',
    });
    return response.data;
  },

  getDiscrepancies: async (filters: {
    report?: number;
    inspection_mission?: number;
    status?: string;
    custody_only?: boolean;
  } = {}): Promise<Discrepancy[]> => {
    const response = await sessionApi.get('/reconciliation-discrepancies/', { params: filters });
    return listResults<Discrepancy>(response.data);
  },

  retainAuthoritativeCustody: async (
    id: number,
    payload: RetainAuthoritativePayload,
  ): Promise<Discrepancy> => {
    const response = await sessionApi.post(
      `/reconciliation-discrepancies/${id}/retain-authoritative/`,
      payload,
    );
    return response.data;
  },

  referForCustodyCorrection: async (
    id: number,
    payload: ReferCustodyCorrectionPayload,
  ): Promise<Discrepancy> => {
    const response = await sessionApi.post(
      `/reconciliation-discrepancies/${id}/refer-custody-correction/`,
      payload,
    );
    return response.data;
  },

  prescribeCorrectionPlan: async (
    id: number,
    payload: CorrectionPlanPayload,
  ): Promise<Discrepancy> => {
    const response = await sessionApi.post(
      `/reconciliation-discrepancies/${id}/correction-plan/`,
      payload,
    );
    return response.data;
  },

  revalidateAfterCorrection: async (
    id: number,
    payload: RevalidateCorrectionPayload = {},
  ): Promise<Discrepancy> => {
    const response = await sessionApi.post(
      `/reconciliation-discrepancies/${id}/revalidate/`,
      payload,
    );
    return response.data;
  },

  generateReconciliationPdf: async (reconciliationReport: number) => {
    const response = await sessionApi.post('/generated-reports/reconciliation/', {
      reconciliation_report: reconciliationReport,
    });
    return response.data;
  },

  generateCompliancePdf: async (inspection: number) => {
    const response = await sessionApi.post('/generated-reports/compliance/', {
      inspection,
    });
    return response.data;
  },
};
